import React from "react";
import clsx from "clsx";
import { format, startOfMonth, endOfMonth, startOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";

const localeMap = { pt: ptBR, en: enUS };
const weekDaysMap = {
  pt: ["D", "S", "T", "Q", "Q", "S", "S"],
  en: ["S", "M", "T", "W", "T", "F", "S"],
};
const weekStartMap = { pt: 0, en: 0 };

type Props = {
  value: Date;
  onChange: (date: Date) => void;
  locale: "pt" | "en";
  theme: "light" | "dark";
};

const MiniMonthCalendar: React.FC<Props> = ({ value, onChange, locale, theme }) => {
  const [month, setMonth] = React.useState(() => value);

  const weekDays = weekDaysMap[locale];
  const weekStartsOn = weekStartMap[locale];
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const startDate = startOfWeek(monthStart, { weekStartsOn: weekStartsOn as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
  const endDate = endOfMonth(monthEnd);

  // Gera matriz de semanas
  const weeks: Date[][] = [];
  let day = startDate;
  while (day <= endDate) {
    const week: Date[] = [];
    for (let d = 0; d < 7; d++) {
      week.push(day);
      day = addDays(day, 1);
    }
    weeks.push(week);
  }

  return (
    <div className={clsx("p-2 rounded-lg font-grotesk", theme === "dark" ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900")}>
      <div className="flex items-center justify-between mb-2 font-inter">
        <button
          type="button"
          onClick={() => setMonth(subMonths(month, 1))}
          className="px-1 text-lg"
          aria-label="Mês anterior"
        >‹</button>
        <span className="font-semibold text-sm font-inter" style={{ minWidth: 80, textAlign: "center" }}>
          {format(month, "MMM yyyy", { locale: localeMap[locale] })}
        </span>
        <button
          type="button"
          onClick={() => setMonth(addMonths(month, 1))}
          className="px-1 text-lg"
          aria-label="Próximo mês"
        >›</button>
      </div>
      <table className="w-full text-xs font-grotesk">
        <thead>
          <tr>
            {weekDays.map((wd, i) => (
              <th key={i} className="text-center font-normal">{wd}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map((dayD, di) => (
                <td key={di} className="p-0.5 text-center">
                  <button
                    type="button"
                    onClick={() => { onChange(dayD); setMonth(dayD); }}
                    className={clsx(
                      "w-6 h-6 rounded-full flex items-center justify-center",
                      isSameDay(dayD, value) && (theme === "dark" ? "bg-cyan-600 text-white" : "bg-blue-600 text-white"),
                      !isSameMonth(dayD, month) && "opacity-40"
                    )}
                    tabIndex={-1}
                  >
                    {format(dayD, "d")}
                  </button>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MiniMonthCalendar;